import { useQuery } from '@apollo/client'
import { ReactNode, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'

import CustomLoading from '@/components/Loading'
import { useDefaultAuthRedirect } from '@/pages/SignIn/utils'
import { PATHS_WITH_SECTIONS } from '@/router/RoutesUtils'
import { recordInviteVisit } from '@/utils/api/recordInviteVisit'
import {
  bootParamsService,
  ParameterName,
} from '@/services/BootParams/BootParamsService'
import { parseInviteSlug } from '@/utils/slugify'

import type { SignInQueryResult } from './AuthRoute'
import { IS_LOGGED_IN } from './queries'

interface InviteAuthGuardProps {
  children: ReactNode
}

const InviteAuthGuard = ({ children }: InviteAuthGuardProps) => {
  const { data, loading } = useQuery<SignInQueryResult>(IS_LOGGED_IN)
  const defaultAuthRedirect = useDefaultAuthRedirect()
  const wasLoggedInOnMount = useRef<boolean | null>(null)
  const hasRecordedVisit = useRef(false)

  // Latch the initial auth state once the query resolves. Subsequent cache
  // flips (e.g. sign-in fired mid account-creation) must not trigger redirect.
  if (!loading && wasLoggedInOnMount.current === null) {
    wasLoggedInOnMount.current = !!data?.isLoggedIn
  }

  useEffect(() => {
    if (loading || !wasLoggedInOnMount.current || hasRecordedVisit.current)
      return

    const refParam = bootParamsService.get(ParameterName.REF)
    if (typeof refParam !== 'string') return

    const { organizationId } = parseInviteSlug(refParam)
    if (!organizationId) return

    hasRecordedVisit.current = true
    recordInviteVisit(organizationId)
  }, [loading])

  if (loading) {
    return <CustomLoading />
  }

  if (wasLoggedInOnMount.current) {
    // Existing customers who arrive from an "upgrade" CTA (e.g. the paid-tier
    // buttons on the marketing site) should land on the in-app payments page
    // rather than the default dashboard. Other logged-in arrivals (invited
    // attorneys/admins, stray navigations) keep the default redirect.
    const wantsUpgrade =
      bootParamsService.get(ParameterName.INTENT) === 'upgrade'
    const paymentsPath = `${PATHS_WITH_SECTIONS.ADMIN_PANEL}/payments`
    return (
      <Navigate
        to={wantsUpgrade ? paymentsPath : defaultAuthRedirect}
        replace
      />
    )
  }

  return children
}

export default InviteAuthGuard
